import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useBoPostingReadBack } from '@/hooks/trades/use-bo-posting-read-back';
import { getErrorMessage } from '@/utils/get-error-message';
import { postingLabel, RECORD_FROM_BO_COPY } from '@/utils/trades/bo-posting-copy';

/**
 * LR-706 (g): the rows BackOffice holds for a legacy posting, and the decision to
 * write them into our book.
 *
 * A confirm dialog with a body it has to FETCH, which is the whole point: the
 * operator is about to put money rows in the ledger, and the only honest way to
 * ask is to show them what will be written — account, asset, BackOffice's own
 * figure beside the one this trade would post today, and BackOffice's uuid, which
 * becomes each row's `external_id`.
 *
 * The two amount columns are not decoration. A pending trade can be amended
 * between the posting BackOffice kept and this moment; the local row records
 * BackOffice's figure (LR-710 `_reconcile_amount`), so a difference is something
 * the person confirming must see beforehand rather than read in a log after.
 *
 * A refusal from the server arrives as a 400 whose `detail` IS the answer — "only
 * half the posting is there", "an empty answer is not proof" — so it is rendered
 * where the body would be and the confirm stays disabled. Nothing here retries:
 * asking BackOffice the same question three times does not change what it holds.
 */
export interface RecordFromBackofficeDialogProps {
  show: boolean;
  tradeId: number;
  onClose: () => void;
  /** Called with the uuids the operator saw — the server re-checks them. */
  onConfirm: (uuids: string[]) => void;
  isRecording: boolean;
  recordError?: Error | null;
}

export function RecordFromBackofficeDialog({
  show,
  tradeId,
  onClose,
  onConfirm,
  isRecording,
  recordError,
}: RecordFromBackofficeDialogProps) {
  const readBack = useBoPostingReadBack(tradeId, show);
  const rows = readBack.data?.transactions ?? [];
  const uuids = readBack.data?.uuids ?? [];
  // Which posting this is, in the panel's own words. A DVP transfer that timed out
  // leaves TWO stuck postings, and this dialog is about one of them — naming it
  // here is what lets the operator check that the rows below are the leg they
  // clicked from (LR-706 (g) round 2).
  const what = readBack.data ? postingLabel(readBack.data) : '';
  // Confirming an empty list would ask the server to write nothing and answer
  // with a refusal the operator did not need to see.
  const ready = !readBack.isLoading && !readBack.isError && rows.length > 0;

  return (
    <ConfirmDialog
      show={show}
      onClose={onClose}
      title={what ? `${RECORD_FROM_BO_COPY.dialogTitle}: ${what}` : RECORD_FROM_BO_COPY.dialogTitle}
      titleColor="red"
      confirmLabel="Record"
      confirmColor="red"
      isLoading={isRecording}
      confirmDisabled={!ready}
      onConfirm={() => onConfirm(uuids)}
      // The write's own failure. The read's failure is shown in the body instead,
      // because it replaces what the dialog is about rather than annotating it.
      error={recordError}
      errorFallback="Failed to record the posting. Please try again."
      showResponseDetail
    >
      {readBack.isLoading && (
        <p className="text-sm text-gray-500">{RECORD_FROM_BO_COPY.loading}</p>
      )}

      {readBack.isError && (
        <div
          data-testid="record-from-bo-refusal"
          className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700"
        >
          {/* The server's `detail` IS the answer here, and an AxiosError's own
              `message` is "Request failed with status code 400" — which tells the
              operator nothing about what BackOffice said. */}
          {getErrorMessage(readBack.error, RECORD_FROM_BO_COPY.emptyFallback)}
        </div>
      )}

      {ready && (
        <>
          <p className="text-sm text-gray-500" data-testid="record-from-bo-lead">
            BackOffice holds these transactions for the <strong>{what}</strong> of
            trade {tradeId}. {RECORD_FROM_BO_COPY.dialogLead}
          </p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-[11px]">
              <thead className="text-gray-500">
                <tr>
                  <th className="py-1 pr-3 font-medium">Account</th>
                  <th className="py-1 pr-3 font-medium">Asset</th>
                  <th className="py-1 pr-3 font-medium">BackOffice</th>
                  <th className="py-1 pr-3 font-medium">Ours</th>
                  <th className="py-1 font-medium">BackOffice id</th>
                </tr>
              </thead>
              <tbody data-testid="record-from-bo-rows">
                {rows.map((row) => {
                  const differs = row.bo_amount !== row.our_amount;
                  return (
                    <tr key={row.uuid} className="border-t border-gray-100">
                      <td className="py-1 pr-3 font-mono">{row.account}</td>
                      <td className="py-1 pr-3">{row.asset}</td>
                      <td
                        className={
                          differs ? 'py-1 pr-3 font-medium text-red-700' : 'py-1 pr-3'
                        }
                      >
                        {row.bo_amount}
                      </td>
                      <td
                        className={
                          differs ? 'py-1 pr-3 text-red-700' : 'py-1 pr-3 text-gray-500'
                        }
                      >
                        {row.our_amount}
                      </td>
                      <td className="py-1 font-mono text-gray-500">{row.uuid}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            {RECORD_FROM_BO_COPY.dialogEffect}
          </p>
        </>
      )}
    </ConfirmDialog>
  );
}
